import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import DataGrid from 'devextreme/ui/data_grid';
import { NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { COMPACT_LAYOUT_MAX_WIDTH } from './shared/utils/layout';
import { environment } from '@env/environment';
import { AuthService } from '@services/auth.service';
import { Router } from '@angular/router';
import { BnNgIdleService } from 'bn-ng-idle';
import { UserService } from './services/user.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  title = 'hersotek-portal';
  @ViewChild('sidenav') sidenav?: MatSidenav;
  @ViewChild('sidenavContent', { read: ElementRef }) sidenavContent?: ElementRef<HTMLElement>;
  private contentResizeObserver?: ResizeObserver;
  // Below this CSS width (tablet, or laptops at high OS scaling) the menu is an
  // overlay that starts closed, so the content keeps the full width.
  isCompact = window.innerWidth < COMPACT_LAYOUT_MAX_WIDTH;
  private isLoggedIn = false;

  @HostListener('window:resize')
  onResize(): void {
    const compact = window.innerWidth < COMPACT_LAYOUT_MAX_WIDTH;
    if (compact === this.isCompact) return;
    this.isCompact = compact;
    if (compact) this.sidenav?.close();
    else if (this.isLoggedIn) this.sidenav?.open();
  }

  // Receives the template ref: the header emits during the first render, before
  // the @ViewChild query is resolved.
  onLoggedIn(sidenav: MatSidenav): void {
    this.isLoggedIn = true;
    if (!this.isCompact) sidenav.open();
  }

  // The content width changes without a window resize when the side menu opens or
  // closes, and when the autosize container applies its margin after a grid has
  // already measured. DevExtreme grids only re-measure (column hiding, widths) when
  // the window size changes, so re-measure them here.
  ngAfterViewInit(): void {
    const content = this.sidenavContent?.nativeElement;
    if (!content || typeof ResizeObserver === 'undefined') return;
    let lastWidth = content.clientWidth;
    let timer: ReturnType<typeof setTimeout> | undefined;
    this.contentResizeObserver = new ResizeObserver(() => {
      if (content.clientWidth === lastWidth) return;
      lastWidth = content.clientWidth;
      // Debounced: the menu animation changes the width on every frame.
      clearTimeout(timer);
      timer = setTimeout(() => {
        content.querySelectorAll('dx-data-grid').forEach(el => (DataGrid.getInstance(el) as DataGrid | undefined)?.updateDimensions());
      }, 150);
    });
    this.contentResizeObserver.observe(content);
  }

  ngOnDestroy(): void {
    this.contentResizeObserver?.disconnect();
  }

  // Logout / idle timeout: the menu must not reopen on /login when resizing.
  onLoggedOut(sidenav: MatSidenav): void {
    this.isLoggedIn = false;
    sidenav.close();
  }

  constructor(
    private authSvc: AuthService,
    private router: Router,
    private bnIdle: BnNgIdleService,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(() => {
      if (this.isCompact) this.sidenav?.close();
    });

    this.bnIdle
      .startWatching(environment.EXPIRATION_SESSION)
      .subscribe((res) => {
        if (res) {
          this.authSvc.logout().subscribe();
          this.router.navigate(['login']);
        }
      });
  }
}
